import { useEffect, useState } from "react";
import api, { resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, Edit3, Trash2, Star, Upload, X, Video } from "lucide-react";

const empty = () => ({
  title: "", title_fr: "", description: "", description_fr: "",
  event_type: "wedding", video_url: "", thumbnail_url: "",
  photos: [], date_held: "", location: "", guest_count: 0,
  testimonial_text: "", testimonial_author: "", featured: false, order: 0,
});

const EVENT_TYPES = ["wedding", "birthday", "baptism", "private", "corporate", "community", "other"];

export default function AdminEventRealizations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/event-realizations");
      setItems(r.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setEditing(null); setForm(empty()); };

  const onEdit = (it) => { setEditing(it); setForm({ ...empty(), ...it, photos: it.photos || [] }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, guest_count: parseInt(form.guest_count) || 0, order: parseInt(form.order) || 0 };
      if (editing) {
        await api.patch(`/admin/event-realizations/${editing.id}`, payload);
        toast.success("Réalisation mise à jour");
      } else {
        await api.post("/admin/event-realizations", payload);
        toast.success("Réalisation créée");
      }
      setOpen(false); reset(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de sauvegarde");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    await api.delete(`/admin/event-realizations/${id}`);
    toast.success("Supprimé");
    load();
  };

  const uploadFile = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (field === "photos") {
        setForm((f) => ({ ...f, photos: [...(f.photos || []), r.data.url] }));
      } else {
        setForm((f) => ({ ...f, [field]: r.data.url }));
      }
      toast.success("Upload OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6" data-testid="admin-event-realizations-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-bold text-2xl md:text-3xl text-[var(--dd-green)]">Dakar Events · Réalisations</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">{items.length} réalisation(s) — portfolio public</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <button className="btn-mustard !py-2.5" data-testid="add-realization-button"><Plus size={14} /> Nouvelle réalisation</button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-[var(--dd-green)]">{editing ? "Modifier" : "Nouvelle"} réalisation</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Titre (EN)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="real-form-title" /></div>
                <div><Label>Titre (FR)</Label><Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="mt-1" /></div>
                <div>
                  <Label>Type d'événement</Label>
                  <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-1 text-sm bg-white" data-testid="real-form-type">
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label>Date de l'événement</Label><Input type="date" value={form.date_held} onChange={(e) => setForm({ ...form, date_held: e.target.value })} className="mt-1" /></div>
                <div><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" placeholder="Brooklyn NY" /></div>
                <div><Label>Nombre d'invités</Label><Input type="number" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} className="mt-1" /></div>
              </div>

              <div>
                <Label>Description (EN)</Label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <Label>Description (FR)</Label>
                <textarea rows={3} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-1 text-sm" />
              </div>

              {/* Video */}
              <div className="border-2 border-dashed border-black/10 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold"><Video size={16} /> Vidéo</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px]">URL externe (YouTube embed, Vimeo, etc.)</Label>
                    <Input value={form.video_url?.startsWith("http") ? form.video_url : ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="mt-1" data-testid="real-form-video-url" />
                  </div>
                  <div>
                    <Label className="text-[10px]">OU uploader une vidéo MP4 (max 25MB)</Label>
                    <input type="file" accept="video/*" onChange={(e) => uploadFile(e.target.files?.[0], "video_url")} className="mt-1 text-xs w-full" data-testid="real-form-video-upload" disabled={uploading} />
                  </div>
                </div>
                {form.video_url && !form.video_url.startsWith("http") && (
                  <div className="text-xs text-[var(--dd-green)]">✓ Vidéo uploadée : {form.video_url}</div>
                )}
              </div>

              {/* Thumbnail */}
              <div className="border-2 border-dashed border-black/10 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold"><Upload size={16} /> Miniature (image de couverture)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="URL externe" />
                  <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files?.[0], "thumbnail_url")} className="text-xs" disabled={uploading} />
                </div>
                {form.thumbnail_url && <img src={resolveImage(form.thumbnail_url)} alt="thumb" className="h-24 rounded-xl object-cover" />}
              </div>

              {/* Photos gallery */}
              <div className="border-2 border-dashed border-black/10 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold"><Upload size={16} /> Galerie photos</div>
                <input type="file" accept="image/*" multiple onChange={async (e) => {
                  for (const f of Array.from(e.target.files || [])) await uploadFile(f, "photos");
                }} className="text-xs" disabled={uploading} data-testid="real-form-photos-upload" />
                {form.photos?.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {form.photos.map((p, i) => (
                      <div key={i} className="relative group">
                        <img src={resolveImage(p)} alt={`p${i}`} className="aspect-square object-cover rounded-xl" />
                        <button onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Témoignage client</Label><textarea rows={3} value={form.testimonial_text} onChange={(e) => setForm({ ...form, testimonial_text: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-1 text-sm" placeholder="Une nuit inoubliable…" /></div>
                <div><Label>Auteur du témoignage</Label><Input value={form.testimonial_author} onChange={(e) => setForm({ ...form, testimonial_author: e.target.value })} className="mt-1" placeholder="Aïcha D., Mariée" /></div>
                <div><Label>Ordre d'affichage</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1" /></div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[var(--dd-green)]" data-testid="real-form-featured" />
                <span className="text-sm">Mettre en avant (Featured)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-[var(--dsf-border)]">
              <button onClick={() => setOpen(false)} className="btn-pill-outline">Annuler</button>
              <button onClick={save} className="btn-mustard" data-testid="real-form-submit">{editing ? "Mettre à jour" : "Créer"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && <div className="col-span-full text-center py-16 text-[var(--dsf-text-muted)]">Aucune réalisation — créez la première</div>}
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-[var(--dsf-border)] rounded-2xl overflow-hidden" data-testid={`realization-row-${it.id}`}>
            <div className="aspect-[4/3] bg-[var(--dd-bone)] relative">
              {it.thumbnail_url ? <img src={resolveImage(it.thumbnail_url)} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--dsf-text-muted)]">No image</div>}
              {it.featured && <div className="absolute top-2 left-2 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full flex items-center gap-1"><Star size={10} /> Featured</div>}
              {it.video_url && <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full flex items-center gap-1"><Video size={10} /> Vidéo</div>}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold mb-1">{it.event_type}</div>
              <h3 className="font-semibold text-[var(--dd-green)] leading-snug">{it.title_fr || it.title}</h3>
              <div className="text-xs text-[var(--dsf-text-muted)] mt-1">{it.location} · {it.guest_count} invités</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => onEdit(it)} className="flex-1 btn-pill-outline !py-1.5 !text-xs" data-testid={`real-edit-${it.id}`}><Edit3 size={12} /> Modifier</button>
                <button onClick={() => remove(it.id)} className="w-9 h-9 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-coral)]" data-testid={`real-delete-${it.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
